import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon, HomeIcon, SearchIcon, ArrowLeftIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
interface PartnerBranding {
  name: string;
  logo: string;
  primaryColor: string;
  accentColor: string;
  backUrl: string;
  programTitle?: string;
}
export function ThankYouPage() {
  usePageMeta('Thank You | FindOnlineColleges', 'Your information request has been submitted. A school representative will be in touch shortly.', {
    ogImage: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const location = useLocation();
  const formData = (location.state as any)?.formData;
  const partner = (location.state as any)?.partner as PartnerBranding | undefined;
  if (partner) {
    return <main className="min-h-screen flex flex-col" style={{
      backgroundColor: '#F8FAFB'
    }} data-id="element-2635" data-mp="1no9cjus6dxwi">
        {/* Partner Header */}
        <header className="bg-white py-4 px-6 sm:px-12 flex items-center justify-center shadow-sm border-b border-gray-100" data-id="element-2636" data-mp="3mqt1d17w0uvl">
          {partner.logo ? <img src={partner.logo} alt={`${partner.name} Logo`} className="h-14 object-contain" data-id="element-2637" data-mp="3vs9a74j20oxp" /> : <span className="text-xl font-bold" style={{
          color: partner.primaryColor
        }} data-id="element-2638" data-mp="4ewh5c323cph">
              {partner.name}
            </span>}
        </header>

        {/* Hero */}
        <div className="relative overflow-hidden py-16 md:py-24" style={{
        background: `linear-gradient(135deg, ${partner.primaryColor} 0%, ${partner.primaryColor}dd 60%, ${partner.accentColor}88 100%)`
      }} data-id="element-2639" data-mp="ecavn65f5rf5">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-2640" data-mp="nch7knlgyjc7">
            <motion.div initial={{
            scale: 0,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20,
            delay: 0.1
          }} className="mx-auto mb-6" data-id="element-2641" data-mp="2l4bq0cfyw83b">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto border-2" style={{
              backgroundColor: `${partner.accentColor}20`,
              borderColor: `${partner.accentColor}40`
            }} data-id="element-2642" data-mp="3ni983gwdtacw">
                <CheckCircleIcon className="w-10 h-10" style={{
                color: partner.accentColor
              }} data-id="element-2643" data-mp="f99c5lzopx8x" />
              </div>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.25
          }} className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2644" data-mp="a9wgqcuqzhuq">
              Thank You{formData?.firstName ? `, ${formData.firstName}` : ''}!
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.35
          }} className="text-lg text-white/90 mb-2" data-id="element-2645" data-mp="t1p7s1q9aszt">
              Your information has been submitted to{' '}
              <strong className="text-white" data-id="element-2646" data-mp="13kzoj556qbp2">{partner.name}</strong>.
            </motion.p>

            {partner.programTitle && <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="text-base text-white/70" data-id="element-2647" data-mp="17ls1o9h6h0qm">
                Program: {partner.programTitle}
              </motion.p>}

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.45
          }} className="text-base text-white/70 max-w-md mx-auto mt-3" data-id="element-2648" data-mp="jvpbjywtq4un">
              A representative from {partner.name} will reach out to you shortly
              to discuss your education options.
            </motion.p>
          </div>
        </div>

        {/* Steps Card */}
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20 pb-16 w-full" data-id="element-2649" data-mp="2xpiubr2o1xj">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.55
        }} className="bg-white rounded-xl shadow-lg p-8" data-id="element-2650" data-mp="3u9r29nqy1c6q">
            <h2 className="text-lg font-bold mb-4" style={{
            color: partner.primaryColor
          }} data-id="element-2651" data-mp="3l8147z2k3ezz">
              What Happens Next?
            </h2>
            <div className="space-y-4" data-id="element-2652" data-mp="3bcpyg694li4d">
              <div className="flex items-start gap-4" data-id="element-2653" data-mp="37am9cetjaf0m">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                backgroundColor: `${partner.primaryColor}15`
              }} data-id="element-2654" data-mp="37bxkx0t80r2a">
                  <span className="text-sm font-bold" style={{
                  color: partner.primaryColor
                }} data-id="element-2655" data-mp="15kkjwmudj3bi">
                    1
                  </span>
                </div>
                <div data-id="element-2656" data-mp="2seo3ttjhejr5">
                  <p className="font-semibold text-gray-800" data-id="element-2657" data-mp="2se4j652g5h9f">
                    Application Review
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2658" data-mp="3189ilcvnc10k">
                    {partner.name} will review your information and match you
                    with the best program options.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-2659" data-mp="29hwdj05iv4dl">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                backgroundColor: `${partner.primaryColor}15`
              }} data-id="element-2660" data-mp="2dioqa3dlrrem">
                  <span className="text-sm font-bold" style={{
                  color: partner.primaryColor
                }} data-id="element-2661" data-mp="3oxvgzy472c7i">
                    2
                  </span>
                </div>
                <div data-id="element-2662" data-mp="1tuozkvg1ynfn">
                  <p className="font-semibold text-gray-800" data-id="element-2663" data-mp="1tu5re6g7fdet">
                    Expect a Call or Email
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2664" data-mp="1psbwyfwlr5n3">
                    An admissions advisor from {partner.name} will contact you
                    {formData?.email ? ` at ${formData.email}` : ''} to discuss
                    next steps.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-id="element-2665" data-mp="1b4qrwgg62wcj">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                backgroundColor: `${partner.primaryColor}15`
              }} data-id="element-2666" data-mp="165mltyojheqf">
                  <span className="text-sm font-bold" style={{
                  color: partner.primaryColor
                }} data-id="element-2667" data-mp="1g2scx6x5l5qn">
                    3
                  </span>
                </div>
                <div data-id="element-2668" data-mp="newlagmfy40q">
                  <p className="font-semibold text-gray-800" data-id="element-2669" data-mp="ndlgcdh9eq81">
                    Enroll & Get Started
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2670" data-mp="2z0f3jm5wqlmr">
                    Once you're ready, your advisor at {partner.name} will guide
                    you through the enrollment process.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3" data-id="element-2671" data-mp="2u3x5f5531lje">
              <button onClick={() => navigate(partner.backUrl)} className="flex items-center justify-center gap-2 px-6 py-3 text-white font-semibold rounded-md transition-colors text-sm" style={{
              backgroundColor: partner.primaryColor
            }} onMouseEnter={e => e.currentTarget.style.opacity = '0.9'} onMouseLeave={e => e.currentTarget.style.opacity = '1'} data-id="element-2672" data-mp="3hspt6ebjwn2x">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2673" data-mp="3dspecskji9vc" />
                Back to {partner.name}
              </button>
              <button onClick={() => navigate('/')} className="flex items-center justify-center gap-2 px-6 py-3 border font-semibold rounded-md transition-colors text-sm" style={{
              borderColor: partner.primaryColor,
              color: partner.primaryColor
            }} data-id="element-2674" data-mp="22bpos4pku950">
                <HomeIcon className="w-4 h-4" data-id="element-2675" data-mp="2p2k4ysd668ap" />
                Explore More Schools
              </button>
            </div>
          </motion.div>
        </div>
      </main>;
  }
  // Default generic thank you page
  return <main className="flex-grow bg-light" data-id="element-2676" data-mp="3cpijvlln586i">
      <div className="hero-bg relative overflow-hidden py-16 md:py-24" data-id="element-2677" data-mp="3cogx4jv9ix8y">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-2678" data-mp="37qfchui6upvd">
          <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.1
        }} className="mx-auto mb-6" data-id="element-2679" data-mp="32zff6o55q5sc">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto border border-white/20" data-id="element-2680" data-mp="3qp1edblj4l0c">
              <CheckCircleIcon className="w-10 h-10 text-gold" data-id="element-2681" data-mp="1wv215qweofy" />
            </div>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.25
        }} className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2682" data-mp="6un0kim1t8t4">
            Thank You{formData?.firstName ? `, ${formData.firstName}` : ''}!
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.35
        }} className="text-lg text-gray-300 mb-2" data-id="element-2683" data-mp="psqy0fskpjf3">
            Your information has been submitted successfully.
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.45
        }} className="text-base text-gray-400 max-w-md mx-auto" data-id="element-2684" data-mp="14iy7w74ilu46">
            A representative from one of our partner schools will reach out to
            you shortly to discuss your education options.
          </motion.p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20 pb-16" data-id="element-2685" data-mp="52k6bgn8kbl7">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.55
      }} className="bg-white rounded-xl shadow-lg p-8" data-id="element-2686" data-mp="9v3pct4ozyxe">
          <h2 className="text-lg font-bold text-navy mb-4" data-id="element-2687" data-mp="it5rjc05wxm9">
            What Happens Next?
          </h2>
          <div className="space-y-4" data-id="element-2688" data-mp="numm6ps1znue">
            <div className="flex items-start gap-4" data-id="element-2689" data-mp="sso59aphbn5v">
              <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-2690" data-mp="ss4zsth0mr8q">
                <span className="text-sm font-bold text-navy" data-id="element-2691" data-mp="2pnj8j8reyctt">1</span>
              </div>
              <div data-id="element-2692" data-mp="2umdp041wrpar">
                <p className="font-semibold text-gray-800" data-id="element-2693" data-mp="2n0rz823aloar">
                  Review Your Matches
                </p>
                <p className="text-sm text-gray-600" data-id="element-2694" data-mp="2i9icpad1j133">
                  Our system is matching you with accredited programs that fit
                  your goals and preferences.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-id="element-2695" data-mp="28d6vc5ylfl1s">
              <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-2696" data-mp="35zcorppjclma">
                <span className="text-sm font-bold text-navy" data-id="element-2697" data-mp="3109t2vvg3rqz">2</span>
              </div>
              <div data-id="element-2698" data-mp="2w0x7eegb8qpz">
                <p className="font-semibold text-gray-800" data-id="element-2699" data-mp="2w0f4iyz57e93">
                  Expect a Call or Email
                </p>
                <p className="text-sm text-gray-600" data-id="element-2700" data-mp="2r1tz8l51a4ds">
                  An admissions advisor from a matched school will contact you
                  {formData?.email ? ` at ${formData.email}` : ''} to discuss
                  next steps.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-id="element-2701" data-mp="7o9brl69ambk">
              <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-2702" data-mp="bp34qsyk2rbb">
                <span className="text-sm font-bold text-navy" data-id="element-2703" data-mp="3tvr7wda1p4ec">3</span>
              </div>
              <div data-id="element-2704" data-mp="2o53ujjeyzwy">
                <p className="font-semibold text-gray-800" data-id="element-2705" data-mp="2ndazgh7bgad">
                  Enroll & Get Started
                </p>
                <p className="text-sm text-gray-600" data-id="element-2706" data-mp="3k3v5wr4u8u4u">
                  Once you've found the right fit, your advisor will guide you
                  through the enrollment process.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3" data-id="element-2707" data-mp="3g31irzz7zjd0">
            <button onClick={() => navigate('/')} className="flex items-center justify-center gap-2 px-6 py-3 bg-navy hover:bg-navy-light text-white font-semibold rounded-md transition-colors text-sm" data-id="element-2708" data-mp="3g16ucboqp946">
              <HomeIcon className="w-4 h-4" data-id="element-2709" data-mp="3677zzyuwqtms" />
              Back to Home
            </button>
            <button onClick={() => navigate('/colleges')} className="flex items-center justify-center gap-2 px-6 py-3 border border-navy text-navy hover:bg-navy/5 font-semibold rounded-md transition-colors text-sm" data-id="element-2710" data-mp="2mleqj45wqep0">
              <SearchIcon className="w-4 h-4" data-id="element-2711" data-mp="2riy4e2yp0pgf" />
              Browse More Schools
            </button>
          </div>
        </motion.div>
      </div>
    </main>;
}